
import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Database, 
  Users, 
  ArrowRight, 
  Lock,
  Heart,
  Zap,
  Key,
  Smartphone,
  CheckCircle,
  HelpCircle,
  Fingerprint,
  PenTool,
  FileSearch,
  Clock,
  Menu,
  X,
  Sparkles,
  Shield,
  HandHeart,
  Globe,
  Mail,
  Building2,
  ChevronRight,
  FileBadge,
  PlayCircle,
  Orbit,
  Cpu,
  Share2,
  History,
  TrendingUp,
  CreditCard,
  Target,
  User
} from 'lucide-react';
import { LegalPageType } from '../types';

interface LandingPageProps {
  onStart: (mode: 'login' | 'signup') => void;
  onNavigate: (page: LegalPageType) => void;
}

const HeritageFlowModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-8 animate-fade-in">
      {/* Immersive Dark Backdrop */}
      <div className="absolute inset-0 bg-slate-950/98 backdrop-blur-2xl" onClick={onClose}></div>
      
      {/* Responsive Modal Container */}
      <div className="relative w-full h-full md:h-auto md:max-w-6xl bg-white md:rounded-[3.5rem] shadow-[0_0_100px_rgba(79,70,225,0.3)] overflow-hidden border border-white/10 flex flex-col md:max-h-[95vh] animate-slide-up">
        
        {/* Adaptive Header */}
        <div className="flex items-center justify-between px-6 py-5 md:px-12 md:py-8 border-b border-slate-50">
          <div className="flex items-center gap-3 md:gap-4">
             <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-600 rounded-xl md:rounded-2xl text-white shadow-lg flex items-center justify-center">
               <Cpu size={20} className="animate-pulse" />
             </div>
             <div>
                <h2 className="text-lg md:text-2xl font-black text-slate-900 tracking-tight leading-none">Successor Protocol</h2>
                <div className="flex items-center gap-2 mt-1">
                   <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                   <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Interactive Guide v2.1</p>
                </div>
             </div>
          </div>
          <button onClick={onClose} className="p-3 bg-slate-50 text-slate-400 hover:text-rose-600 rounded-xl transition-all">
            <X size={20} strokeWidth={3} />
          </button>
        </div>

        {/* Adaptive Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-12 h-full">
            
            {/* Left: The Visual Engine (Hidden or scaled on mobile) */}
            <div className="lg:col-span-7 relative h-[350px] md:h-[550px] bg-slate-50/50 border-b lg:border-b-0 lg:border-r border-slate-100 flex items-center justify-center overflow-hidden">
               
               {/* Background Grid */}
               <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
                  <svg width="100%" height="100%"><pattern id="grid-pattern-v2" width="30" height="30" patternUnits="userSpaceOnUse"><path d="M 30 0 L 0 0 0 30" fill="none" stroke="#000" strokeWidth="1"/></pattern><rect width="100%" height="100%" fill="url(#grid-pattern-v2)" /></svg>
               </div>

               {/* Central Scalable Vault */}
               <div className="relative z-20 scale-75 md:scale-100">
                  <div className={`w-32 h-32 md:w-44 md:h-44 bg-white rounded-[3rem] shadow-2xl flex flex-col items-center justify-center border-4 transition-all duration-1000 ${activeStep === 0 ? 'border-indigo-600 scale-110' : 'border-slate-100'}`}>
                     <div className="absolute -inset-10 border border-indigo-100 rounded-full animate-[spin_20s_linear_infinite] opacity-40"></div>
                     <ShieldCheck size={48} md-size={56} className={`transition-all duration-700 ${activeStep === 0 ? 'text-indigo-600 scale-110' : 'text-slate-200'}`} />
                     {activeStep === 0 && (
                        <div className="absolute -bottom-4 px-3 py-1 bg-slate-900 text-white text-[8px] font-black uppercase tracking-widest rounded-lg shadow-xl animate-bounce">
                           Vault Docked
                        </div>
                     )}
                  </div>
               </div>

               {/* Step 1: Responsive Asset Docking */}
               <div className={`absolute inset-0 transition-all duration-1000 ${activeStep === 0 ? 'opacity-100' : 'opacity-0 scale-90'}`}>
                  {/* HDFC Bank Card */}
                  <div className="absolute top-[10%] left-[10%] md:top-20 md:left-20 w-40 md:w-48 p-4 bg-white rounded-2xl shadow-xl border border-indigo-50 animate-[float_4s_ease-in-out_infinite]">
                     <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600"><Building2 size={16}/></div>
                        <div className="text-[10px] font-bold text-slate-800">HDFC Savings</div>
                     </div>
                     <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden"><div className="w-full h-full bg-indigo-500 animate-[width_2s_ease-out]"></div></div>
                  </div>

                  {/* Zerodha / Stocks Card */}
                  <div className="absolute bottom-[15%] right-[10%] md:bottom-24 md:right-24 w-40 md:w-48 p-4 bg-white rounded-2xl shadow-xl border border-emerald-50 animate-[float_5s_ease-in-out_infinite_reverse]">
                     <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600"><TrendingUp size={16}/></div>
                        <div className="text-[10px] font-bold text-slate-800">Equity Portfolio</div>
                     </div>
                     <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden"><div className="w-4/5 h-full bg-emerald-500 animate-[width_3s_ease-out]"></div></div>
                  </div>
               </div>

               {/* Step 2: The Handshake Flow */}
               <div className={`absolute inset-0 z-30 flex items-center justify-center px-6 md:px-12 transition-all duration-700 ${activeStep === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <div className="w-full max-w-lg flex items-center justify-between relative">
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 md:w-24 md:h-24 bg-indigo-600 rounded-3xl flex items-center justify-center text-white shadow-2xl"><User size={32} /></div>
                        <span className="text-[9px] font-black uppercase text-indigo-600 tracking-widest">Owner</span>
                     </div>
                     
                     <div className="flex-1 mx-4 md:mx-10 h-px bg-slate-200 relative">
                        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-4 h-4 bg-indigo-500 rounded-full blur-sm animate-[flow_2s_infinite]"></div>
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-slate-300"><Share2 size={16} className="animate-pulse" /></div>
                     </div>

                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 md:w-24 md:h-24 bg-rose-500 rounded-3xl flex items-center justify-center text-white shadow-2xl"><Users size={32} /></div>
                        <span className="text-[9px] font-black uppercase text-rose-600 tracking-widest">Nominee</span>
                     </div>
                  </div>
               </div>

               {/* Step 3: Holographic Successor Portal */}
               <div className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${activeStep === 2 ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-40 opacity-0 scale-90'}`}>
                  <div className="w-[240px] md:w-[280px] h-[400px] md:h-[480px] bg-slate-900 rounded-[2.5rem] p-4 shadow-2xl border-[6px] md:border-[8px] border-slate-800 relative">
                     <div className="w-12 h-1 bg-slate-700 rounded-full mx-auto mb-6"></div>
                     <div className="space-y-4">
                        <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                           <div className="text-[8px] font-bold text-slate-400 uppercase mb-1">Estate Handover</div>
                           <div className="flex items-center gap-2">
                              <CheckCircle size={14} className="text-emerald-500" />
                              <div className="text-[10px] font-bold text-white">4 Claims Identified</div>
                           </div>
                        </div>
                        <div className="space-y-2 opacity-60">
                           {[1,2,3].map(i => (
                             <div key={i} className="h-8 bg-white/5 rounded-lg w-full flex items-center px-3">
                                <div className="w-12 h-1 bg-white/20 rounded-full"></div>
                             </div>
                           ))}
                        </div>
                        <div className="absolute bottom-10 left-4 right-4 py-3 bg-emerald-500 rounded-xl text-center text-white font-black text-[10px] uppercase tracking-widest shadow-lg animate-pulse">
                           Begin Verified Claim
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            {/* Right: Narrative Content */}
            <div className="lg:col-span-5 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <div className="mb-12">
                <h4 className="text-[10px] md:text-[12px] font-black text-indigo-600 uppercase tracking-[0.4em] mb-4 flex items-center gap-2">
                   <Target size={14} className="fill-indigo-600" /> Core Protocol
                </h4>
                <h3 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight leading-[1.1] mb-6">Innovative <br className="hidden md:block"/>Succession.</h3>
                <p className="text-slate-500 text-sm md:text-lg font-medium leading-relaxed">Most legacies are lost to institutional friction. NomineeDesk replaces confusion with a secure, guided roadmap.</p>
              </div>

              <div className="space-y-3">
                {[
                  { icon: Database, title: "1. Encrypted Docking", desc: "Map your HDFC, LIC, and Zerodha holdings to your central profile securely.", color: "indigo" },
                  { icon: Share2, title: "2. Secure Handshake", desc: "Add nominees with a click. We transmit a cryptographic registration invite instantly.", color: "rose" },
                  { icon: Smartphone, title: "3. Successor Portal", desc: "Heirs gain a clear, institution-specific roadmap to claim their heritage without stress.", color: "emerald" }
                ].map((step, i) => (
                  <button 
                    key={i} 
                    onClick={() => setActiveStep(i)}
                    className={`flex items-start gap-4 p-5 rounded-3xl border-2 transition-all text-left ${activeStep === i ? 'bg-white border-indigo-100 shadow-xl' : 'bg-transparent border-transparent opacity-50'}`}
                  >
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shrink-0 transition-all ${activeStep === i ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                      <step.icon size={20} />
                    </div>
                    <div>
                      <h5 className={`font-black text-sm md:text-base ${activeStep === i ? 'text-slate-900' : 'text-slate-400'}`}>{step.title}</h5>
                      <p className={`text-[11px] md:text-xs font-medium leading-relaxed mt-1 ${activeStep === i ? 'text-slate-500' : 'text-slate-300'}`}>{step.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="px-6 py-6 md:px-12 md:py-8 bg-slate-50 border-t border-slate-100 flex flex-row justify-between items-center gap-4">
           <div className="flex gap-2">
              {[0,1,2].map(dot => (
                <button 
                   key={dot} 
                   onClick={() => setActiveStep(dot)} 
                   className={`h-2 rounded-full transition-all duration-700 ${activeStep === dot ? 'w-10 bg-indigo-600 shadow-md' : 'w-2 bg-slate-200 hover:bg-slate-300'}`} 
                />
              ))}
           </div>
           <button onClick={onClose} className="px-10 py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-2xl hover:bg-indigo-600 transition-all shadow-xl active:scale-95">
              Exit Protocol
           </button>
        </div>
      </div>

      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
        @keyframes flow { 0% { left: 0; opacity: 0; } 50% { opacity: 1; } 100% { left: 100%; opacity: 0; } }
        @keyframes width { from { width: 0; } to { width: 100%; } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        @media (max-width: 640px) {
          .animate-slide-up { animation: slideUpMobile 0.5s ease-out forwards; }
          @keyframes slideUpMobile { from { transform: translateY(100%); } to { transform: translateY(0); } }
        }
      `}</style>
    </div>
  );
};

const LivingLegacyHero = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
    <div className="blob w-[600px] h-[600px] bg-indigo-100/50 -top-20 -left-20" style={{ animationDelay: '0s' }}></div>
    <div className="blob w-[500px] h-[500px] bg-fuchsia-100/40 bottom-20 -right-20" style={{ animationDelay: '-5s' }}></div>
    <div className="blob w-[400px] h-[400px] bg-sky-100/30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ animationDelay: '-10s' }}></div>

    <div className="absolute inset-0 safety-mesh opacity-30 md:opacity-10">
      <svg width="100%" height="100%">
        <pattern id="safety-grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#4f46e1" strokeWidth="0.5" />
          <circle cx="0" cy="0" r="1.5" fill="#4f46e1" />
        </pattern>
        <rect width="100%" height="100%" fill="url(#safety-grid)" />
      </svg>
    </div>

    <div className="hidden lg:block">
      <div className="absolute top-1/4 left-[10%] floating" style={{ animationDelay: '0s' }}>
        <div className="p-6 bg-white/40 backdrop-blur-md rounded-[2rem] border border-white shadow-2xl shadow-indigo-100">
          <Shield className="text-indigo-600 w-12 h-12" />
        </div>
      </div>
      <div className="absolute top-[20%] right-[12%] floating" style={{ animationDelay: '1s' }}>
        <div className="p-5 bg-white/40 backdrop-blur-md rounded-[1.5rem] border border-white shadow-2xl shadow-fuchsia-100">
          <HandHeart className="text-fuchsia-600 w-10 h-10" />
        </div>
      </div>
      <div className="absolute bottom-[25%] left-[15%] floating" style={{ animationDelay: '2s' }}>
        <div className="p-4 bg-white/40 backdrop-blur-md rounded-[1.25rem] border border-white shadow-2xl shadow-sky-100">
          <Key className="text-sky-600 w-8 h-8" />
        </div>
      </div>
      <div className="absolute bottom-[20%] right-[15%] floating" style={{ animationDelay: '0.5s' }}>
        <div className="p-7 bg-white/40 backdrop-blur-md rounded-[2.5rem] border border-white shadow-2xl shadow-indigo-100">
          <Globe className="text-indigo-400 w-14 h-14" />
        </div>
      </div>
    </div>

    <svg width="100%" height="100%" viewBox="0 0 1440 800" className="absolute top-0 left-0 w-full h-full opacity-10">
      <path d="M-100 400 Q 300 200, 720 400 T 1540 400" fill="none" stroke="#4f46e1" strokeWidth="2" strokeDasharray="10 10" />
      <circle cx="720" cy="400" r="300" fill="none" stroke="#4f46e1" strokeWidth="0.5" className="pulse-glow" />
    </svg>
  </div>
);

export const LandingPage: React.FC<LandingPageProps> = ({ onStart, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);

  const handleLegalClick = (type: LegalPageType) => {
    onNavigate(type);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900 relative">
      <HeritageFlowModal isOpen={isProcessModalOpen} onClose={() => setIsProcessModalOpen(false)} />

      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/60 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="text-2xl font-heading font-black tracking-tighter text-slate-900">
              Nominee<span className="text-indigo-600">Desk</span>
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => onStart('login')} className="hidden sm:block text-xs font-bold uppercase tracking-[0.2em] text-slate-900 hover:text-indigo-600 transition-colors px-4 py-2 font-heading">Log In</button>
            <button onClick={() => onStart('signup')} className="bg-slate-900 text-white text-[10px] font-bold uppercase tracking-[0.25em] px-8 py-4 rounded-full hover:bg-indigo-600 transition-all shadow-xl hover:shadow-indigo-100 font-heading">Get Started</button>
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 p-8 flex flex-col gap-6 animate-fade-in shadow-2xl">
            <button onClick={() => { onStart('login'); setMobileMenuOpen(false); }} className="text-left text-xs font-bold uppercase tracking-widest text-indigo-600 font-heading">Log In</button>
            <button onClick={() => { onStart('signup'); setMobileMenuOpen(false); }} className="text-left text-xs font-bold uppercase tracking-widest text-slate-900 font-heading">Get Started</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-20 md:pt-40 md:pb-24 overflow-hidden">
        <LivingLegacyHero />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center animate-slide-up relative z-10">
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-white/60 backdrop-blur-sm text-indigo-600 rounded-full mb-8 border border-indigo-50 shadow-sm font-heading">
            <Sparkles size={16} className="text-fuchsia-500" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">India’s centralized nominee & asset platform</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold text-slate-900 leading-[0.92] tracking-[-0.05em] mb-8">Protect your <br/><span className="text-gradient">Digital Legacy.</span></h1>
          
          <div className="max-w-4xl mx-auto mb-12 px-4 space-y-6 animate-fade-in">
            <p className="font-heading text-xl sm:text-3xl md:text-4xl text-slate-900 font-bold tracking-tight leading-[1.15]">
              Everything your family may need to know — <br className="hidden md:block"/> in one secure place.
            </p>
            <p className="text-base sm:text-xl md:text-2xl text-slate-500 font-medium leading-relaxed tracking-tight max-w-2xl mx-auto">
              Banking, insurance, investments, retirement, property, digital assets,
              employer benefits, professional & government records — and more.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 px-4">
            <button onClick={() => onStart('signup')} className="group w-full sm:w-auto px-10 py-6 md:px-14 md:py-7 bg-indigo-600 text-white rounded-full font-bold text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-4 shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-105 transition-all font-heading">Initialize Your Vault <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" /></button>
            <button onClick={() => setIsProcessModalOpen(true)} className="flex items-center gap-3 px-8 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-indigo-50 shadow-sm font-bold text-indigo-600 text-base sm:text-lg hover:text-indigo-900 hover:bg-white transition-all font-heading group"><PlayCircle size={24} className="group-hover:scale-110 transition-transform" /><span>See how it works</span></button>
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section className="py-16 sm:py-24 relative overflow-hidden bg-slate-50/50">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-50/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-3xl mb-12 md:mb-16">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.6em] text-indigo-500 mb-6 font-heading">The Core Standards</h2>
            <h3 className="font-heading text-4xl sm:text-7xl font-extrabold text-slate-900 tracking-tighter leading-[0.95] mb-8">
              Modern Nominee<span className="text-indigo-600">Desk</span> <br className="hidden sm:block"/>Protocol.
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {[
              { 
                num: "01",
                icon: Heart, 
                color: "rose", 
                title: "Care, Thoughtfully Structured", 
                p: "NomineeDesk helps you clearly record what matters — not just assets, but intentions — so your family never has to search, assume, or struggle." 
              },
              { 
                num: "02",
                icon: Database, 
                color: "indigo", 
                title: "One Trusted Registry", 
                p: "Banking, insurance, investments, retirement, property, digital wallets, employer benefits, government records — all mapped in one secure system." 
              },
              { 
                num: "03",
                icon: Zap, 
                color: "emerald", 
                title: "Clarity for Nominees", 
                p: "Nominees receive a calm, read-only view that explains what exists, where to approach, and how to proceed — without confusion or exposure." 
              }
            ].map((feat, i) => (
              <div key={i} className="group relative bg-white p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] border border-slate-200/60 shadow-sm hover:shadow-2xl hover:border-white transition-all duration-500 flex flex-col h-full">
                <div className="absolute top-10 right-10 text-5xl md:text-6xl font-black text-slate-50 transition-colors group-hover:text-slate-100/50 pointer-events-none">{feat.num}</div>
                <div className={`w-14 h-14 md:w-16 md:h-16 mb-6 md:mb-8 bg-${feat.color}-50 text-${feat.color}-600 rounded-2xl md:rounded-3xl flex items-center justify-center transition-all duration-700 group-hover:scale-110 group-hover:rotate-3 shadow-inner`}><feat.icon size={28} /></div>
                <h4 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-4 leading-tight tracking-tight">{feat.title}</h4>
                <p className="text-slate-500 text-sm md:text-lg leading-relaxed font-medium tracking-tight mb-6 flex-grow">{feat.p}</p>
                <div className={`w-12 h-1.5 bg-${feat.color}-100 rounded-full transition-all duration-500 group-hover:w-full group-hover:bg-${feat.color}-600`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blueprint Grid */}
      <section className="py-16 sm:py-24 relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12 mb-12 md:mb-16">
            <div>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.5em] text-indigo-600 mb-6 font-heading">The Master Specification</h2>
              <h3 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tighter leading-[1.05]">The Heritage <br className="hidden sm:block"/> Blueprint.</h3>
            </div>
            <div className="max-w-md hidden md:block">
               <p className="text-slate-400 font-sketch text-2xl leading-relaxed italic opacity-70">"A technical response to the 6 core dimensions of digital succession."</p>
               <div className="mt-6 flex gap-3"><div className="w-12 h-px bg-slate-200"></div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              { q: "What is it?", l: "The Unified Layer", icon: HelpCircle, color: "indigo", p: "Clarity is what families lack — not intent. Most people plan responsibly — they save, invest, insure, and nominate. The real problem is that this information is scattered." },
              { q: "Why do I need it?", l: "Unclaimed Asset Shield", icon: FileSearch, color: "rose", p: "Information Is Scattered. Bank accounts, investments, insurance policies, retirement benefits, digital wallets, and documents live in different places — making it hard for families to even know what exists." },
              { q: "Who is it for?", l: "ASSET OWNERS & NOMINEES", icon: Users, color: "emerald", p: "Asset owners who want to leave clarity behind, and nominees who need visibility — not complexity or guesswork." },
              { q: "When is it relevant?", l: "READY WHEN NEEDED", icon: Clock, color: "amber", p: "NomineeDesk is set up in advance and becomes important exactly when a nominee needs clarity — not after confusion begins." },
              { q: "Where is the data?", l: "SECURE & READ-ONLY", icon: Lock, color: "slate", p: "All information is securely encrypted. No balances. No transactions. Only visibility and guidance." },
              { q: "How does it help?", l: "SIMPLE, GUIDED ACCESS", icon: Fingerprint, color: "indigo", p: "Nominees see assets, allocations, and next-step guidance in one calm, read-only view — without confusion or financial risk." },
            ].map((dim, i) => (
              <div key={i} className="group vellum-effect p-8 md:p-12 rounded-[2.5rem] md:rounded-[3rem] border border-slate-200/50 hover:bg-white/95 hover:shadow-[0_40px_80px_rgba(0,0,0,0.06)] transition-all duration-500 cursor-default relative overflow-hidden">
                <dim.icon className="absolute -right-6 -top-6 w-32 md:w-40 h-32 md:h-40 text-slate-50 opacity-10 group-hover:opacity-20 transition-opacity" />
                <div className={`w-12 h-12 md:w-14 md:h-14 bg-${dim.color === 'slate' ? 'slate' : dim.color}-50 text-${dim.color === 'slate' ? 'slate' : dim.color}-600 rounded-xl md:rounded-2xl flex items-center justify-center mb-6 group-hover:bg-${dim.color === 'slate' ? 'slate-600' : dim.color + '-600'} group-hover:text-white transition-all shadow-sm`}><dim.icon size={28} /></div>
                <h4 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mb-2 tracking-tight">{dim.q}</h4>
                <div className={`text-[10px] font-bold uppercase tracking-[0.3em] text-${dim.color === 'slate' ? 'slate' : dim.color}-600 mb-4 font-heading`}>{dim.l}</div>
                <p className="text-slate-500 text-xs md:text-sm leading-relaxed font-medium tracking-tight whitespace-pre-line">{dim.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Protocol */}
      <section className="py-20 sm:py-32 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 safety-mesh">
           <svg width="100%" height="100%"><pattern id="tech-grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="#fff" strokeWidth="0.5" /></pattern><rect width="100%" height="100%" fill="url(#tech-grid)" /></svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 md:mb-24">
             <h2 className="text-[12px] font-bold uppercase tracking-[0.6em] text-emerald-400 mb-6 font-heading">The Fulfillment Logic</h2>
             <h3 className="font-heading text-4xl sm:text-7xl font-extrabold text-white tracking-tight leading-none">Engineering <br className="hidden md:block"/> Perfection.</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-16">
             <div className="group">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-indigo-600 transition-all"><ShieldCheck className="text-indigo-400 group-hover:text-white" size={32} /></div>
                <h4 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">Silent Guardian</h4>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed font-medium tracking-tight">Connect your assets securely. We bridge the gap between you and your heirs silently in the background.</p>
             </div>
             <div className="group">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-emerald-600 transition-all"><Key className="text-emerald-400 group-hover:text-white" size={32} /></div>
                <h4 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">Heritage Access</h4>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed font-medium tracking-tight">When triggered, heirs receive instant, guided visibility into their specific allocations across all banks.</p>
             </div>
             <div className="group">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-amber-600 transition-all"><Smartphone className="text-amber-400 group-hover:text-white" size={32} /></div>
                <h4 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">Guided Claim</h4>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed font-medium tracking-tight">No legal confusion. Automated, institution-specific claim paths for a stress-free transition.</p>
             </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-16 bg-white border-t border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
           <div className="flex items-center justify-center mb-8"><span className="font-heading text-2xl font-black tracking-tighter uppercase text-slate-900">Nominee<span className="text-indigo-600">Desk</span></span></div>
           <div className="flex flex-wrap justify-center gap-6 md:gap-8 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-12 font-heading">
              <button onClick={() => handleLegalClick('privacy')} className="hover:text-slate-900 transition-colors">Privacy</button>
              <button onClick={() => handleLegalClick('terms')} className="hover:text-slate-900 transition-colors">Terms</button>
              <button onClick={() => handleLegalClick('faq')} className="hover:text-slate-900 transition-colors">FAQ</button>
              <button onClick={() => handleLegalClick('security')} className="hover:text-slate-900 transition-colors">Security</button>
              <button onClick={() => handleLegalClick('compliance')} className="hover:text-slate-900 transition-colors">Compliance</button>
              <button onClick={() => handleLegalClick('contact')} className="hover:text-slate-900 transition-colors">Contact</button>
           </div>
           <p className="text-slate-400 text-[9px] md:text-[10px] font-bold tracking-[0.4em] uppercase font-heading">© 2026 NomineeDesk. Secure Digital Succession Systems.</p>
        </div>
      </footer>
    </div>
  );
};
